// components/Search.tsx
import {
  Box,
  Group,
  Popover,
  ScrollArea,
  Text,
  TextInput,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { SafeImage } from "components/SafeImage ";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProductStore } from "stores/useProductStore"; // Update path as needed
import { debounce } from "utils/helper";

const HeaderSearchBox = () => {
  const [searchValue, setSearchValue] = useState("");
  const [popoverOpened, setPopoverOpened] = useState(false);
  const navigate = useNavigate();

  const allProducts = useProductStore((s) => s.allProducts);
  //const fetchAllProducts = useProductStore((s) => s.fetchInitialProducts);
  const fetchAllProducts = useProductStore.getState().fetchInitialProducts;
  const syncModifiedProducts = useProductStore((s) => s.syncModifiedProducts);
  const store = useProductStore();

  // run only once
  useEffect(() => {
    setTimeout(() => {
      (async () => {
        await fetchAllProducts();
      })();
    }, 0); // Executes after current call stack clears

    const interval = setInterval(
      () => {
        if (document.visibilityState === "visible") {
          syncModifiedProducts();
        }
      },
      1000 * 60 * 5,
    ); // 5-minute sync

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const debouncedSync = debounce(() => {
      if (document.visibilityState === "visible") {
        syncModifiedProducts();
      }
    }, 500); // 500ms debounce after navigation
    debouncedSync(); // prevent immediate sync after route change
    setPopoverOpened(false);
    setSearchValue("");
    // ✅ Prevent lingering sync after route change. This ensures that any delayed function execution is cleaned up when the component unmounts or the dependency changes.
    return () => {
      debouncedSync.cancel();
    };
  }, [location.pathname]);

  const filteredProducts = useMemo(() => {
    return allProducts.items.filter(
      (p) =>
        p.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
        p.productCode.toString().includes(searchValue.toLowerCase()),
    );
  }, [searchValue, allProducts]);

  return (
    <Box style={{ marginLeft: "auto" }}>
      <Popover
        opened={popoverOpened && searchValue.length > 0}
        width={380}
        position="bottom-end"
        offset={4}
        trapFocus={false}
        transitionProps={{ transition: "pop-top-left", duration: 150 }}
      >
        <Popover.Target>
          <TextInput
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.currentTarget.value);
              setPopoverOpened(e.currentTarget.value.length >= 3);
            }}
            leftSection={<IconSearch size={16} />}
            style={{ width: 250 }}
          />
        </Popover.Target>

        <Popover.Dropdown>
          <ScrollArea h={300}>
            {filteredProducts.length === 0 ? (
              <Text size="sm" color="dimmed">
                No products found.
              </Text>
            ) : (
              filteredProducts.map((p) => (
                <Box
                  key={p.productCode}
                  onClick={() => {
                    setPopoverOpened(false);
                    navigate(`/products/details/${p.productCode}`);
                  }}
                  style={{
                    cursor: "pointer",
                    padding: "6px 4px",
                    borderRadius: 4,
                    transition: "background-color 120ms ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f9f9f9";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <Group mb="xs" align="center" wrap="nowrap">
                    <SafeImage
                      src={`/images/${p.featuredImageName || "no-image.png"}`}
                      width={48}
                      height={48}
                      style={{
                        minWidth: 48,
                        maxWidth: 48,
                        minHeight: 48,
                        maxHeight: 48,
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                      radius="sm"
                    />
                    <Box>
                      <Text size="sm" fw={500}>
                        {p.title}
                      </Text>
                      {p.soh === 0 && (
                        <Text size="xs" c="red" mt={2}>
                          Currently not available
                        </Text>
                      )}
                    </Box>
                  </Group>
                </Box>
              ))
            )}
          </ScrollArea>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
};

export default HeaderSearchBox;
